// Compute sales-vs-LY percentages on the fly from foundational sales windows
// stored on ForecastNoosDecomposition. Aggregates across an arbitrary set of
// variants by summing recent/LY sales before dividing — the only mathematically
// correct way to combine pct values across variants. CAGR fields stay
// pre-computed and are not aggregatable.

import { formatNumber } from "~/utils/formatting"
import { formatName } from "~/utils/namingUtils"

type DecompositionInput = {
  salesRecent1m: number | null
  salesLy1m: number | null
  salesRecent3m: number | null
  salesLy3m: number | null
  salesRecent6m: number | null
  salesLy6m: number | null
  salesRecent12m: number | null
  salesLy12m: number | null
}

type VariantLike = {
  forecastDecomposition?: DecompositionInput | null
}

export type AggregatedSalesVsLy = {
  salesVsLy1mPct: number | null
  salesVsLy3mPct: number | null
  salesVsLy6mPct: number | null
  salesVsLy12mPct: number | null
}

export type SalesVsLyWindow = 1 | 3 | 6 | 12

export type SalesVsLyAggregate = {
  pct: number | null
  /** Variants carrying both sides of this window — the population `pct` was measured over. */
  eligible: number
  /** Variants offered to the aggregate, whether or not they carry the window. */
  total: number
}

// A variant is eligible for a given window only if it has both a recent and a
// LY value and LY > 0. Ineligible variants (typically: no last-year sales) are
// excluded entirely so they don't skew the aggregated numerator — which makes
// `pct` a figure over `eligible`, not over the set the reader is looking at.
// The gap is wide and tenant-specific (hummel: 78% of variants on 5W, 70% on
// 52W; tekla: 55% on 5W and none at all on 52W), so the counts travel with the
// pct and every surface that collapses more than one variant discloses them.
export function salesVsLyByWindow(
  variants: VariantLike[],
  window: SalesVsLyWindow,
): SalesVsLyAggregate {
  const recentKey = `salesRecent${window}m` as keyof DecompositionInput
  const lyKey = `salesLy${window}m` as keyof DecompositionInput
  let recentSum = 0
  let lySum = 0
  let eligibleCount = 0
  for (const v of variants) {
    const d = v.forecastDecomposition
    if (!d) continue
    const recent = d[recentKey]
    const ly = d[lyKey]
    if (typeof recent !== "number" || typeof ly !== "number" || ly <= 0) continue
    recentSum += recent
    lySum += ly
    eligibleCount++
  }
  return {
    pct: eligibleCount === 0 ? null : ((recentSum - lySum) / lySum) * 100,
    eligible: eligibleCount,
    total: variants.length,
  }
}

/**
 * What an aggregated pct covers, for a surface presenting it as the set's number. Undefined where
 * there is nothing to disclose: no figure at all, or every variant in the set behind it.
 */
export function salesVsLyCoverageNote({
  pct,
  eligible,
  total,
}: SalesVsLyAggregate): string | undefined {
  if (pct == null || eligible >= total) return undefined
  return `Over ${formatNumber(eligible)} of ${formatNumber(total)} variants; the rest have no ${formatName("sale", true)} last year.`
}

export function aggregateSalesVsLy(variants: VariantLike[]): AggregatedSalesVsLy {
  return {
    salesVsLy1mPct: salesVsLyByWindow(variants, 1).pct,
    salesVsLy3mPct: salesVsLyByWindow(variants, 3).pct,
    salesVsLy6mPct: salesVsLyByWindow(variants, 6).pct,
    salesVsLy12mPct: salesVsLyByWindow(variants, 12).pct,
  }
}
